import React, { useState, useEffect } from 'react';
import {
  Ticket,
  Clock,
  AlertTriangle,
  CheckCircle,
  User,
  Phone,
  MapPin,
  Calendar,
  Filter,
  Search,
  Plus,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Wrench,
  Timer,
  TrendingUp,
  Users,
  Zap,
  Monitor,
  HardDrive,
  Download,
  Package,
  Smartphone,
  Settings,
  Eye,
  MessageSquare,
  PhoneCall,
  Globe,
  Bot
} from 'lucide-react';

// Tabs del panel SAT segun documentacion
const TABS = [
  { id: 'incidencias', label: 'Incidencias', icon: Ticket },
  { id: 'sucesos', label: 'Sucesos', icon: Bot },
  { id: 'sat_externo', label: 'SAT Externo', icon: Users },
  { id: 'pantallas', label: 'Pantallas Averias', icon: Monitor },
  { id: 'inventario', label: 'Inventario', icon: Package },
  { id: 'dispositivos', label: 'Dispositivos', icon: HardDrive },
  { id: 'archivos', label: 'Archivos', icon: Download }
];

// Origenes de incidencia con iconos
const INCIDENT_ORIGINS = {
  telefono: { icon: PhoneCall, label: 'Telefono XIA', color: 'blue' },
  whatsapp: { icon: MessageSquare, label: 'WhatsApp', color: 'green' },
  aparato: { icon: Monitor, label: 'Aparato XIA', color: 'purple' },
  chat: { icon: Globe, label: 'Chat interno', color: 'orange' }
};

// Componente de tarjeta de ticket/incidencia
const TicketCard = ({ ticket, onClick }) => {
  const priorityColors = {
    critica: 'border-l-red-500 bg-red-50',
    alta: 'border-l-orange-500 bg-orange-50',
    media: 'border-l-yellow-500 bg-yellow-50',
    baja: 'border-l-green-500 bg-green-50'
  };

  const statusBadge = {
    abierta: 'bg-red-50 text-red-700',
    en_curso: 'bg-yellow-50 text-yellow-700',
    resuelta: 'bg-green-50 text-green-700'
  };

  const origin = INCIDENT_ORIGINS[ticket.origen] || INCIDENT_ORIGINS.chat;
  const OriginIcon = origin.icon;

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border border-gray-100 border-l-4 ${priorityColors[ticket.prioridad]} p-4 cursor-pointer hover:shadow-md transition-all`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#3B82F6]">#{ticket.id}</span>
            <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${statusBadge[ticket.estado]}`}>
              {ticket.estado.replace('_', ' ')}
            </span>
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs bg-${origin.color}-50 text-${origin.color}-700`}>
              <OriginIcon className="w-3 h-3" />
              {origin.label}
            </span>
          </div>
          <h4 className="font-medium text-[#111827] mt-1">{ticket.asunto}</h4>
          <p className="text-sm text-[#6B7280] mt-1 line-clamp-2">{ticket.descripcion}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-[#6B7280]">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {ticket.cliente}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {ticket.tiempo}
          </span>
        </div>
        {ticket.tecnico && (
          <span className="flex items-center gap-1 text-[#3B82F6]">
            <Wrench className="w-3 h-3" />
            {ticket.tecnico}
          </span>
        )}
      </div>
      {ticket.slaRiesgo && (
        <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
          <AlertTriangle className="w-3 h-3" />
          SLA en riesgo - {ticket.slaRestante}
        </div>
      )}
    </div>
  );
};

// Componente de suceso (resuelto por XIA automaticamente)
const SucesoCard = ({ suceso }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-all">
    <div className="flex items-start gap-3">
      <div className="p-2 bg-green-50 rounded-lg">
        <Bot className="w-5 h-5 text-green-600" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#111827]">{suceso.tipo}</span>
          <span className="px-2 py-0.5 rounded-lg text-xs bg-green-50 text-green-700">
            Resuelto por XIA
          </span>
        </div>
        <p className="text-sm text-[#6B7280] mt-1">{suceso.descripcion}</p>
        <div className="flex items-center gap-4 mt-2 text-xs text-[#6B7280]">
          <span>{suceso.cuenta}</span>
          <span>{suceso.tiempo}</span>
        </div>
      </div>
    </div>
  </div>
);

// Componente de tecnico externo
const TechnicianCard = ({ technician }) => {
  const statusColors = {
    disponible: 'bg-green-500',
    ocupado: 'bg-yellow-500',
    ausente: 'bg-gray-400'
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-all">
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-10 h-10 bg-[#3B82F6] rounded-full flex items-center justify-center text-white font-medium">
            {technician.nombre.charAt(0)}
          </div>
          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 ${statusColors[technician.estado]} rounded-full border-2 border-white`}></div>
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-[#111827]">{technician.nombre}</p>
          <p className="text-xs text-[#6B7280]">{technician.provincia}</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-[#111827]">{technician.tarifa}</p>
          <p className="text-xs text-[#6B7280]">{technician.incidenciasHoy} hoy</p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs text-[#6B7280]">
        <Phone className="w-3 h-3" />
        {technician.telefono}
      </div>
    </div>
  );
};

// Componente de dispositivo
const DeviceCard = ({ device }) => {
  const statusColors = {
    online: 'bg-green-500',
    offline: 'bg-red-500',
    warning: 'bg-yellow-500'
  };

  const typeIcons = {
    taquilla: Monitor,
    pinpad: Smartphone,
    impresora: HardDrive,
    pda: Smartphone
  };

  const TypeIcon = typeIcons[device.tipo] || Monitor;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-all">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-gray-100 rounded-lg">
          <TypeIcon className="w-5 h-5 text-gray-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#111827]">{device.id}</span>
            <div className={`w-2 h-2 rounded-full ${statusColors[device.estado]}`}></div>
          </div>
          <p className="text-xs text-[#6B7280] mt-0.5">{device.tipo}</p>
        </div>
        <span className="text-xs text-[#6B7280]">{device.cuenta}</span>
      </div>
      <div className="mt-2 text-xs text-[#6B7280]">
        <span>{device.ciudad}</span>
      </div>
    </div>
  );
};

export default function SATPanel() {
  const [activeTab, setActiveTab] = useState('incidencias');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('todos');
  const [kpisCollapsed, setKpisCollapsed] = useState(false);

  const [kpis] = useState({
    abiertos: { value: 23, criticos: 3 },
    enCurso: { value: 12, porMi: 5 },
    tiempoMedio: { value: '1.8h', tendencia: '-15%' },
    slaCumplido: { value: '96.5%', tendencia: '+2%' },
    sucesosHoy: { value: 45, resueltosPorXia: 42 }
  });

  const [incidencias] = useState([
    { id: '1234', asunto: 'TPV no enciende', descripcion: 'El terminal punto de venta no responde al encendido desde esta manana', cliente: 'Rest. El Molino', prioridad: 'critica', estado: 'abierta', tiempo: 'Hace 15 min', tecnico: null, slaRiesgo: true, slaRestante: '45 min', origen: 'telefono' },
    { id: '1235', asunto: 'Error en impresora tickets', descripcion: 'La impresora no imprime correctamente los tickets, salen cortados', cliente: 'Bar Central', prioridad: 'alta', estado: 'en_curso', tiempo: 'Hace 1h', tecnico: 'Carlos R.', slaRiesgo: false, origen: 'whatsapp' },
    { id: '1236', asunto: 'Lentitud en sistema', descripcion: 'El software va muy lento desde la ultima actualizacion', cliente: 'Hotel Costa', prioridad: 'media', estado: 'en_curso', tiempo: 'Hace 2h', tecnico: 'Ana M.', slaRiesgo: false, origen: 'aparato' },
    { id: '1237', asunto: 'Pantalla tactil no responde', descripcion: 'La pantalla del TPV no detecta el tacto en algunas zonas', cliente: 'Pizzeria Roma', prioridad: 'alta', estado: 'en_curso', tiempo: 'Hace 4h', tecnico: 'Miguel P.', slaRiesgo: true, slaRestante: '30 min', origen: 'chat' }
  ]);

  const [sucesos] = useState([
    { id: 1, tipo: 'Cola impresion bloqueada', descripcion: 'XIA detecto y limpio la cola de impresion automaticamente', cuenta: 'Teatro Real', tiempo: 'Hace 5 min' },
    { id: 2, tipo: 'Reinicio preventivo', descripcion: 'XIA reinicio el servicio de impresion por lentitud detectada', cuenta: 'Alcazar Sevilla', tiempo: 'Hace 15 min' },
    { id: 3, tipo: 'Conexion pinpad restaurada', descripcion: 'XIA detecto desconexion y guio al taquillero a reconectar cable', cuenta: 'Museo Prado', tiempo: 'Hace 30 min' },
    { id: 4, tipo: 'Backup ejecutado', descripcion: 'XIA ejecuto backup manual tras detectar 3 dias sin backup', cuenta: 'Gym Fitness', tiempo: 'Hace 1h' }
  ]);

  const [tecnicosExternos] = useState([
    { id: 'SAT-001', nombre: 'Juan Martinez', provincia: 'Madrid', telefono: '612 345 678', tarifa: '35eur/h', estado: 'disponible', incidenciasHoy: 2 },
    { id: 'SAT-002', nombre: 'Pedro Garcia', provincia: 'Barcelona', telefono: '623 456 789', tarifa: '40eur/h', estado: 'ocupado', incidenciasHoy: 3 },
    { id: 'SAT-003', nombre: 'Maria Lopez', provincia: 'Valencia', telefono: '634 567 890', tarifa: '35eur/h', estado: 'disponible', incidenciasHoy: 1 },
    { id: 'SAT-004', nombre: 'Carlos Ruiz', provincia: 'Sevilla', telefono: '645 678 901', tarifa: '30eur/h', estado: 'ausente', incidenciasHoy: 0 }
  ]);

  const [dispositivos] = useState([
    { id: 'TAQ-001', tipo: 'taquilla', cuenta: 'Teatro Real', ciudad: 'Madrid', estado: 'online' },
    { id: 'TAQ-002', tipo: 'taquilla', cuenta: 'Alcazar Sevilla', ciudad: 'Sevilla', estado: 'online' },
    { id: 'PIN-001', tipo: 'pinpad', cuenta: 'Teatro Real', ciudad: 'Madrid', estado: 'online' },
    { id: 'IMP-001', tipo: 'impresora', cuenta: 'Museo Prado', ciudad: 'Madrid', estado: 'warning' },
    { id: 'TAQ-003', tipo: 'taquilla', cuenta: 'Parque Warner', ciudad: 'Madrid', estado: 'offline' }
  ]);

  const [inventario] = useState([
    { id: 'TAQ-001', tipo: 'Taquilla', ubicacion: 'Teatro Real', anos: 2, precioCompra: '1.200eur', depreciacion: '800eur', propiedad: '100% Manantial' },
    { id: 'PIN-015', tipo: 'Pinpad', ubicacion: 'ALMACEN', anos: 0, precioCompra: '150eur', depreciacion: '150eur', propiedad: '100% Manantial' },
    { id: 'IMP-008', tipo: 'Impresora', ubicacion: 'REPARACION', anos: 3, precioCompra: '450eur', depreciacion: '200eur', propiedad: '60% Franquiciado' }
  ]);

  const filteredIncidencias = incidencias.filter(inc => {
    if (filterStatus === 'todos') return true;
    return inc.estado === filterStatus;
  });

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#111827]">Servicio Tecnico (SAT)</h1>
          <p className="text-[#6B7280] mt-1">Gestion de incidencias, tecnicos y dispositivos</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors">
          <Plus className="w-4 h-4" />
          <span>Nueva incidencia</span>
        </button>
      </div>

      {/* KPIs - Collapsible */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <button
          onClick={() => setKpisCollapsed(!kpisCollapsed)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#3B82F6]" />
            <span className="font-medium text-[#111827]">KPIs del SAT</span>
            {kpisCollapsed && (
              <span className="text-sm text-[#6B7280] ml-2">
                {kpis.abiertos.value} abiertas | {kpis.enCurso.value} en curso | {kpis.slaCumplido.value} SLA
              </span>
            )}
          </div>
          {kpisCollapsed ? (
            <ChevronDown className="w-5 h-5 text-[#6B7280]" />
          ) : (
            <ChevronUp className="w-5 h-5 text-[#6B7280]" />
          )}
        </button>
        {!kpisCollapsed && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 pt-0">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280]">Abiertas</p>
                  <p className="text-xl font-semibold text-[#111827]">{kpis.abiertos.value}</p>
                </div>
                <Ticket className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-xs text-red-600 mt-1">{kpis.abiertos.criticos} criticas</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280]">En curso</p>
                  <p className="text-xl font-semibold text-[#111827]">{kpis.enCurso.value}</p>
                </div>
                <Wrench className="w-5 h-5 text-yellow-500" />
              </div>
              <p className="text-xs text-[#6B7280] mt-1">{kpis.enCurso.porMi} mias</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280]">Sucesos</p>
                  <p className="text-xl font-semibold text-[#111827]">{kpis.sucesosHoy.value}</p>
                </div>
                <Bot className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-xs text-green-600 mt-1">{kpis.sucesosHoy.resueltosPorXia} XIA</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280]">T. medio</p>
                  <p className="text-xl font-semibold text-[#111827]">{kpis.tiempoMedio.value}</p>
                </div>
                <Timer className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-xs text-green-600 mt-1">{kpis.tiempoMedio.tendencia}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280]">SLA</p>
                  <p className="text-xl font-semibold text-[#111827]">{kpis.slaCumplido.value}</p>
                </div>
                <Zap className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-xs text-green-600 mt-1">{kpis.slaCumplido.tendencia}</p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="flex gap-4">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-3 border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-[#3B82F6] text-[#3B82F6]'
                  : 'border-transparent text-[#6B7280] hover:text-[#111827]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content - Incidencias */}
      {activeTab === 'incidencias' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <input
                  type="text"
                  placeholder="Buscar incidencias..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
              >
                <option value="todos">Todos</option>
                <option value="abierta">Abiertas</option>
                <option value="en_curso">En curso</option>
                <option value="resuelta">Resueltas</option>
              </select>
            </div>

            {/* Incidencias list */}
            <div className="space-y-3">
              {filteredIncidencias.map((inc) => (
                <TicketCard key={inc.id} ticket={inc} onClick={() => {}} />
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* SLA en riesgo */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <h3 className="font-semibold text-[#111827]">SLA en riesgo</h3>
              </div>
              <div className="space-y-3">
                {incidencias.filter(t => t.slaRiesgo).map((inc) => (
                  <div key={inc.id} className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                    <div>
                      <p className="text-sm font-medium text-[#111827]">#{inc.id}</p>
                      <p className="text-xs text-[#6B7280]">{inc.cliente}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-red-600">{inc.slaRestante}</p>
                      <p className="text-xs text-[#6B7280]">restante</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content - Sucesos */}
      {activeTab === 'sucesos' && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-green-600" />
              <p className="text-sm text-green-800">
                <strong>Sucesos</strong>: Incidencias que XIA resolvio automaticamente sin intervencion humana
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sucesos.map(suceso => (
              <SucesoCard key={suceso.id} suceso={suceso} />
            ))}
          </div>
        </div>
      )}

      {/* Content - SAT Externo */}
      {activeTab === 'sat_externo' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#6B7280]">
              Tecnicos externos por provincia. Reciben incidencias que XIA no puede resolver remotamente.
            </p>
            <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors">
              <Plus className="w-4 h-4" />
              <span>Nuevo tecnico</span>
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tecnicosExternos.map(tech => (
              <TechnicianCard key={tech.id} technician={tech} />
            ))}
          </div>
        </div>
      )}

      {/* Content - Pantallas Averias */}
      {activeTab === 'pantallas' && (
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <p className="text-sm text-purple-800">
              Configura las pantallas que se muestran en los aparatos cuando hay una averia.
              Personaliza skin, colores, logo y mensaje.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <Monitor className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#111827] mb-2">Configurador de Pantallas</h3>
            <p className="text-[#6B7280] mb-4">Personaliza las pantallas de averia por cuenta</p>
            <button className="px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors">
              Configurar pantallas
            </button>
          </div>
        </div>
      )}

      {/* Content - Inventario */}
      {activeTab === 'inventario' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <input
                type="text"
                placeholder="Buscar en inventario..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors">
              <Plus className="w-4 h-4" />
              <span>Nuevo item</span>
            </button>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase">Ubicacion</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase">Anos</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase">Precio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase">Valor actual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase">Propiedad</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {inventario.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-[#3B82F6]">{item.id}</td>
                    <td className="px-6 py-4 text-sm text-[#111827]">{item.tipo}</td>
                    <td className="px-6 py-4 text-sm text-[#111827]">
                      <span className={`px-2 py-1 rounded text-xs ${
                        item.ubicacion === 'ALMACEN' ? 'bg-blue-50 text-blue-700' :
                        item.ubicacion === 'REPARACION' ? 'bg-yellow-50 text-yellow-700' :
                        'bg-gray-50 text-gray-700'
                      }`}>
                        {item.ubicacion}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#6B7280]">{item.anos}</td>
                    <td className="px-6 py-4 text-sm text-[#111827]">{item.precioCompra}</td>
                    <td className="px-6 py-4 text-sm text-[#111827]">{item.depreciacion}</td>
                    <td className="px-6 py-4 text-sm text-[#6B7280]">{item.propiedad}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Content - Dispositivos */}
      {activeTab === 'dispositivos' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <input
                type="text"
                placeholder="Buscar dispositivos..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
              />
            </div>
            <select className="px-4 py-2 border border-gray-200 rounded-xl text-sm">
              <option>Todos los tipos</option>
              <option>Taquillas</option>
              <option>Pinpads</option>
              <option>Impresoras</option>
            </select>
            <select className="px-4 py-2 border border-gray-200 rounded-xl text-sm">
              <option>Todos los estados</option>
              <option>Online</option>
              <option>Offline</option>
              <option>Warning</option>
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dispositivos.map(device => (
              <DeviceCard key={device.id} device={device} />
            ))}
          </div>
        </div>
      )}

      {/* Content - Archivos */}
      {activeTab === 'archivos' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-800">
              Drivers, software y herramientas accesibles desde <strong>descargas.chatmanantial.com</strong>.
              XIA accede automaticamente para ejecutar reparaciones.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-sm transition-all cursor-pointer">
              <div className="p-3 bg-blue-50 rounded-xl w-fit mb-4">
                <Download className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-medium text-[#111827]">XIA Windows</h3>
              <p className="text-sm text-[#6B7280] mt-1">Instalador para taquillas Windows</p>
              <span className="text-xs text-[#3B82F6] mt-2 block">.exe - 45MB</span>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-sm transition-all cursor-pointer">
              <div className="p-3 bg-green-50 rounded-xl w-fit mb-4">
                <Download className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-medium text-[#111827]">XIA Android</h3>
              <p className="text-sm text-[#6B7280] mt-1">App para PDA y tablets</p>
              <span className="text-xs text-[#3B82F6] mt-2 block">.apk - 28MB</span>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-sm transition-all cursor-pointer">
              <div className="p-3 bg-purple-50 rounded-xl w-fit mb-4">
                <Download className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-medium text-[#111827]">Drivers Impresoras</h3>
              <p className="text-sm text-[#6B7280] mt-1">Pack completo de drivers</p>
              <span className="text-xs text-[#3B82F6] mt-2 block">.zip - 120MB</span>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-sm transition-all cursor-pointer">
              <div className="p-3 bg-orange-50 rounded-xl w-fit mb-4">
                <Download className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-medium text-[#111827]">Herramientas BAT</h3>
              <p className="text-sm text-[#6B7280] mt-1">Scripts de reparacion</p>
              <span className="text-xs text-[#3B82F6] mt-2 block">.bat - 2KB</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
